import {
  queryKeys,
  SUPPORT_CHANNELS,
  type SupportChannel,
  supportTicketAlertsResponseSchema,
} from '@mevaser/shared'
import { useToast } from '@mevaser/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef } from 'react'
import { armAlertSound, playAlertChime } from '../lib/alert-sound.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const CURSOR_KEY = 'mv-ticket-alerts-cursor'
const SOUND_CLAIM_KEY = 'mv-ticket-alerts-sound-claim'
const SEEN_KEY = 'mv-ticket-alerts-seen'
const POLL_MS = 10_000
/** A saved cursor older than this is dropped rather than replayed: a ticket
 *  from an hour ago is not "new", and push/email already covered it. */
const MAX_CURSOR_AGE_MS = 15 * 60_000

const CHANNEL_LABELS: Record<SupportChannel, string> = {
  sms: 'Text message',
  call: 'Phone call',
  web_chat: 'Website chat',
  contact_form: 'Contact form',
  email: 'Email',
  manual: 'Manual',
}

interface AlertTicket {
  id: string
  ticketNumber: string
  subject: string
  channel: SupportChannel
}

const tabId = Math.random().toString(36).slice(2)

function loadCursor(): string | null {
  try {
    const saved = localStorage.getItem(CURSOR_KEY)
    if (!saved) return null
    const age = Date.now() - Date.parse(saved)
    return Number.isFinite(age) && age >= 0 && age < MAX_CURSOR_AGE_MS ? saved : null
  } catch {
    return null
  }
}

function saveCursor(value: string): void {
  try {
    localStorage.setItem(CURSOR_KEY, value)
  } catch {
    // Without storage a reload simply starts from "now".
  }
}

/** Per tab (sessionStorage), not shared: every open tab should show its own
 *  toast, and only a reload of *this* tab needs to avoid replaying them. */
function loadSeen(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) ?? '[]') as string[])
  } catch {
    return new Set()
  }
}

function saveSeen(seen: Set<string>): void {
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-50)))
  } catch {
    // Not worth failing an alert over.
  }
}

/** One chime per ticket across all open tabs: whichever tab writes its id into
 *  the shared claim last, and still finds it there, is the one that plays. */
function claimSound(ticketId: string): boolean {
  try {
    const mine = `${ticketId}:${tabId}`
    const existing = localStorage.getItem(SOUND_CLAIM_KEY)
    if (existing?.startsWith(`${ticketId}:`) && existing !== mine) return false
    localStorage.setItem(SOUND_CLAIM_KEY, mine)
    return localStorage.getItem(SOUND_CLAIM_KEY) === mine
  } catch {
    return true
  }
}

function isChannel(value: unknown): value is SupportChannel {
  return SUPPORT_CHANNELS.includes(value as SupportChannel)
}

/**
 * Raises a toast and a chime when a new customer support ticket arrives
 * (t1078). Renders nothing; mounted once in `AppShell` so it runs on every
 * back-office screen, not just Support Tickets.
 *
 * Two feeds, one handler: a 10-second poll of a small server-side feed (works
 * with no push set up at all), and a message from the service worker when a
 * push lands while this tab is open (makes the toast instant). Both go through
 * `announce`, which de-duplicates by ticket id.
 */
export function TicketAlerts() {
  const { can } = useAuth()
  const enabled = can('support:read')
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const cursor = useRef<string | null>(loadCursor())
  const seen = useRef<Set<string>>(loadSeen())

  const openTicket = useCallback(
    (ticketId: string) => {
      void navigate({ to: '/support-tickets/$ticketId', params: { ticketId } })
    },
    [navigate],
  )

  const announce = useCallback(
    (ticket: AlertTicket) => {
      if (seen.current.has(ticket.id)) return
      seen.current.add(ticket.id)
      saveSeen(seen.current)

      toast.toast({
        tone: 'info',
        title: `New support ticket ${ticket.ticketNumber}`,
        description: `${CHANNEL_LABELS[ticket.channel]} — ${ticket.subject}`,
        action: { label: 'Open', onClick: () => openTicket(ticket.id) },
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      if (claimSound(ticket.id)) void playAlertChime()
    },
    [toast, queryClient, openTicket],
  )

  const { data } = useQuery({
    queryKey: queryKeys.supportTicketAlerts(),
    queryFn: async () => {
      // With no cursor this is the first look since the portal opened: what it
      // finds already existed, so it is remembered but not announced.
      const quiet = cursor.current === null
      const response = await api.get('/support-tickets/alerts', {
        query: { since: cursor.current ?? new Date().toISOString() },
        schema: supportTicketAlertsResponseSchema,
      })
      return { ...response, quiet }
    },
    enabled,
    refetchInterval: POLL_MS,
    // A hidden tab is exactly where the chime is most useful.
    refetchIntervalInBackground: true,
    staleTime: 0,
  })

  useEffect(() => {
    if (!data) return
    for (const ticket of data.tickets) {
      if (data.quiet) seen.current.add(ticket.id)
      else announce(ticket)
    }
    if (data.quiet) saveSeen(seen.current)
    cursor.current = data.serverTime
    saveCursor(data.serverTime)
  }, [data, announce])

  useEffect(() => (enabled ? armAlertSound() : undefined), [enabled])

  useEffect(() => {
    if (!enabled || !('serviceWorker' in navigator)) return
    const onMessage = (event: MessageEvent) => {
      const message = event.data as {
        type?: string
        url?: string
        ticket?: { ticketId?: string; ticketNumber?: string; subject?: string; channel?: unknown }
      } | null
      if (message?.type === 'new-ticket' && message.ticket) {
        const { ticketId, ticketNumber, subject, channel } = message.ticket
        if (ticketId && ticketNumber && isChannel(channel)) {
          announce({ id: ticketId, ticketNumber, subject: subject ?? '', channel })
        }
      } else if (message?.type === 'open-ticket' && message.url?.startsWith('/support-tickets')) {
        const ticketId = message.url.split('/')[2]
        if (ticketId) openTicket(ticketId)
        else void navigate({ to: '/support-tickets' })
      }
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [enabled, announce, openTicket, navigate])

  return null
}
